"use client";

import { X, Trophy, Target, Star, Crown } from "lucide-react";
import Image from "next/image";
import { useEffect, useState } from "react";

export type MilestoneType = 50 | 100 | 200 | "all";

interface MilestonePopupProps {
  isOpen: boolean;
  onClose: () => void;
  milestone: MilestoneType;
  solvedCount: number;
  totalProblems?: number;
}

const milestoneConfig: Record<
  MilestoneType,
  {
    title: string;
    icon: React.ReactNode;
    color: string;
    bgColor: string;
    borderColor: string;
    message: string;
    image: string;
  }
> = {
  50: {
    title: "50문제 달성!",
    icon: <Target className="w-6 h-6 text-blue-500" />,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    message: "시작이 반이에요! 좋은 출발입니다.",
    image: "/main/eureka_gamza.webp",
  },
  100: {
    title: "100문제 달성!",
    icon: <Star className="w-6 h-6 text-yellow-500" />,
    color: "text-yellow-600",
    bgColor: "bg-yellow-50",
    borderColor: "border-yellow-200",
    message: "꾸준히 잘 하고 있어요! 이 기세 그대로!",
    image: "/main/happy_gamza.webp",
  },
  200: {
    title: "200문제 달성!",
    icon: <Trophy className="w-6 h-6 text-orange-500" />,
    color: "text-orange-600",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200",
    message: "대단해요! 합격이 눈앞에 보여요!",
    image: "/main/shocking_gamza.webp",
  },
  all: {
    title: "전체 문제 완료!",
    icon: <Crown className="w-6 h-6 text-purple-500" />,
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    message: "모든 문제를 풀었어요! 합격 가자!",
    image: "/main/study_gamza.webp",
  },
};

export default function MilestonePopup({
  isOpen,
  onClose,
  milestone,
  solvedCount,
  totalProblems,
}: MilestonePopupProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  // 팝업이 열릴 때 애니메이션 시작 + 1초 후 정지 — 타이머 외부 동기화
  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsAnimating(true);
      // 애니메이션 후 정지
      const timer = setTimeout(() => setIsAnimating(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const config = milestoneConfig[milestone];
  const progressPercentage = totalProblems
    ? Math.min(100, Math.round((solvedCount / totalProblems) * 100))
    : 0;

  return (
    <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div
        className={`bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl transform transition-all duration-300 ${
          isAnimating ? "scale-105" : "scale-100"
        }`}
      >
        {/* 모달 헤더 */}
        <div
          className={`flex items-center justify-between p-4 border-b dark:border-gray-700 ${config.bgColor} dark:bg-opacity-20 rounded-t-xl`}
        >
          <h3
            className={`text-lg font-bold flex items-center gap-2 ${config.color}`}
          >
            {config.icon}
            {config.title}
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/50 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <X className="w-4 h-4 dark:text-gray-300" />
          </button>
        </div>

        {/* 모달 내용 */}
        <div className="p-6 text-center">
          <div className="mb-4">
            <Image
              src={config.image}
              alt="축하 감자"
              width={120}
              height={120}
              className={`mx-auto mb-4 ${isAnimating ? "animate-bounce" : ""}`}
            />
            <p className="text-gray-700 dark:text-gray-300 text-lg font-medium mb-2">
              {config.message}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              지금까지{" "}
              <span className={`font-bold ${config.color}`}>{solvedCount}개</span>
              의 문제를 풀었어요!
            </p>
          </div>

          {/* 진행률 바 */}
          {totalProblems && (
            <div className={`${config.bgColor} dark:bg-gray-700 rounded-lg p-4`}>
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                <span>전체 진행률</span>
                <span className="font-medium">
                  {solvedCount} / {totalProblems} ({progressPercentage}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all duration-500 ${
                    milestone === "all"
                      ? "bg-purple-500"
                      : milestone === 200
                      ? "bg-orange-500"
                      : milestone === 100
                      ? "bg-yellow-500"
                      : "bg-blue-500"
                  }`}
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* 모달 푸터 */}
        <div className="flex justify-center p-4 border-t dark:border-gray-700">
          <button
            onClick={onClose}
            className={`px-6 py-2 ${
              milestone === "all"
                ? "bg-purple-500 hover:bg-purple-600"
                : milestone === 200
                ? "bg-orange-500 hover:bg-orange-600"
                : milestone === 100
                ? "bg-yellow-500 hover:bg-yellow-600"
                : "bg-blue-500 hover:bg-blue-600"
            } text-white rounded-lg transition-colors font-medium`}
          >
            계속 풀기
          </button>
        </div>
      </div>
    </div>
  );
}
